import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IdeaMetricsChips from './IdeaMetricsChips';
import { logger } from '../../utils/logger';
import { IdeaDraft } from '../../hooks/useIdeaResults';

interface IdeaMetricsDisplayProps {
  draft: IdeaDraft;
  index: number;
  expandedMetrics: { [key: string]: boolean };
  onToggleMetrics: (draftId: string) => void;
  getImpactColor: (level: string) => string;
  getBaselineDeltaWord: (delta: string) => string;
  simulationRunner?: React.ReactNode;
}

const IdeaMetricsDisplay: React.FC<IdeaMetricsDisplayProps> = ({
  draft,
  index,
  expandedMetrics,
  onToggleMetrics,
  getImpactColor,
  getBaselineDeltaWord,
  simulationRunner
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isVerySmall = useMediaQuery('(max-width:360px)');

  // Parse brandPulseJson to get complete data
  let pulseData: any = {};
  if (draft.brandPulseJson) {
    try {
      pulseData = JSON.parse(draft.brandPulseJson);
    } catch (e) {
      logger.error('Error parsing brandPulseJson:', e);
    }
  }

  const impactColor = pulseData.reputation?.color || draft.reputation?.color || getImpactColor(draft.projectedImpactLevel || 'Unknown');
  // On mobile: Analytics closed by default for all ideas. On desktop: Open for first idea
  const isMetricsExpanded = expandedMetrics[draft.id] ?? (isMobile ? false : index === 0);

  return (
    <>
      {/* Collapsible Additional Analytics */}
      <Accordion 
        expanded={isMetricsExpanded}
        onChange={() => onToggleMetrics(draft.id)}
        sx={{ '&:before': { display: 'none' }, boxShadow: 'none', border: 1, borderColor: 'divider', borderRadius: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            Additional Analytics
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Mobile: 2x3 Metric Grid */}
          {isMobile ? (
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '4px',
              mb: 3
            }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? '#111827' : '#F8F9FA', borderRadius: 1, border: 1, borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E9ECEF' }}>
                <Typography variant="body2" sx={{ color: '#6FA0C8', fontSize: '10px', textTransform: 'uppercase', mb: 1 }}>
                  Replies 1d
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', fontVariantNumeric: 'tabular-nums', fontSize: '14px' }}>
                  {pulseData.replies1d || draft.replies1d || 0}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? '#111827' : '#F8F9FA', borderRadius: 1, border: 1, borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E9ECEF' }}>
                <Typography variant="body2" sx={{ color: '#6FA0C8', fontSize: '10px', textTransform: 'uppercase', mb: 1 }}>
                  Replies 7d
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', fontVariantNumeric: 'tabular-nums', fontSize: '14px' }}>
                  {pulseData.replies7d || draft.replies7d || 0}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? '#111827' : '#F8F9FA', borderRadius: 1, border: 1, borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E9ECEF' }}>
                <Typography variant="body2" sx={{ color: '#6FA0C8', fontSize: '10px', textTransform: 'uppercase', mb: 1 }}>
                  Likes 7d
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', fontVariantNumeric: 'tabular-nums', fontSize: '14px' }}>
                  {pulseData.likes7d || draft.likes7d || 0}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? '#111827' : '#F8F9FA', borderRadius: 1, border: 1, borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E9ECEF' }}>
                <Typography variant="body2" sx={{ color: '#6FA0C8', fontSize: '10px', textTransform: 'uppercase', mb: 1 }}>
                  Confidence
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'capitalize', fontSize: '14px' }}>
                  {pulseData.confidence || draft.confidence || draft.confidenceLevel || 'N/A'}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? '#111827' : '#F8F9FA', borderRadius: 1, border: 1, borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E9ECEF' }}>
                <Typography variant="body2" sx={{ color: '#6FA0C8', fontSize: '10px', textTransform: 'uppercase', mb: 1 }}>
                  Baseline Delta
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'capitalize', fontSize: '14px' }}>
                  {getBaselineDeltaWord(pulseData.delta || draft.delta || 'N/A')}
                </Typography>
              </Box>
              {(pulseData.shares7d !== undefined || draft.shares7d !== undefined || pulseData.saves7d !== undefined || draft.saves7d !== undefined) && (
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? '#111827' : '#F8F9FA', borderRadius: 1, border: 1, borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E9ECEF' }}>
                  <Typography variant="body2" sx={{ color: '#6FA0C8', fontSize: '10px', textTransform: 'uppercase', mb: 1 }}>
                    {pulseData.shares7d !== undefined || draft.shares7d !== undefined ? 'Shares 7d' : 'Saves 7d'}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontVariantNumeric: 'tabular-nums', fontSize: '14px' }}>
                    {pulseData.shares7d || draft.shares7d || pulseData.saves7d || draft.saves7d || 0}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            /* Desktop: Metric Mini-Table with Dividers and Tabular Numbers */
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? '#111827' : '#F8F9FA', borderRadius: 1, border: 1, borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E9ECEF' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Replies (1d)</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}>
                    {pulseData.replies1d || draft.replies1d || 0}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? '#111827' : '#F8F9FA', borderRadius: 1, border: 1, borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E9ECEF' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Replies (7d)</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}>
                    {pulseData.replies7d || draft.replies7d || 0}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? '#111827' : '#F8F9FA', borderRadius: 1, border: 1, borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E9ECEF' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Likes (7d)</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}>
                    {pulseData.likes7d || draft.likes7d || 0}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? '#111827' : '#F8F9FA', borderRadius: 1, border: 1, borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E9ECEF' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Confidence</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                    {pulseData.confidence || draft.confidence || draft.confidenceLevel || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? '#111827' : '#F8F9FA', borderRadius: 1, border: 1, borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E9ECEF' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Baseline Delta</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                    {getBaselineDeltaWord(pulseData.delta || draft.delta || 'N/A')}
                  </Typography>
                </Box>
              </Grid>
              {(pulseData.shares7d !== undefined || draft.shares7d !== undefined) && (
                <Grid item xs={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? '#111827' : '#F8F9FA', borderRadius: 1, border: 1, borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E9ECEF' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Shares (7d)</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}>
                      {pulseData.shares7d || draft.shares7d || 0}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {(pulseData.saves7d !== undefined || draft.saves7d !== undefined) && (
                <Grid item xs={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? '#111827' : '#F8F9FA', borderRadius: 1, border: 1, borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E9ECEF' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Saves (7d)</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}>
                      {pulseData.saves7d || draft.saves7d || 0}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          )}

          {/* Positive Drivers */}
          {(pulseData.driversPositive || draft.driversPositive) && (pulseData.driversPositive || draft.driversPositive).length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#1FAD84', fontWeight: 'bold', mb: 2 }}>
                Positive Drivers
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {(pulseData.driversPositive || draft.driversPositive).map((driver: string, idx: number) => (
                  <Chip 
                    key={idx} 
                    label={driver} 
                    size="small" 
                    sx={{ 
                      bgcolor: '#1FAD84', 
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: 500
                    }} 
                  />
                ))}
              </Stack>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default IdeaMetricsDisplay;
